import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Link,
  Typography,
} from "@mui/material";
import {
  Check as CheckIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Receipt as ReceiptIcon,
  AccountBalance as BankIcon,
  Email as EmailIcon,
  Refresh as RefreshIcon,
  Link as LinkIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { withTranslation } from "react-i18next";
import axios from "axios";
import { useUserStore } from "../../store";
import MaventaActivationDialog from "./MaventaActivationDialog";
import MaventaLinkExistingDialog from "./MaventaLinkExistingDialog";
import AmiliActivationDialog from "./AmiliActivationDialog";
import AmiliDeactivationDialog from "./AmiliDeactivationDialog";

// Status constants matching backend
const STATUS = {
  NOT_ACTIVATED: "not_activated",
  PENDING_SIGNUP: "pending_signup",
  AWAITING_SIGNATURE: "awaiting_signature",
  ACTIVE: "active",
  FAILED: "failed",
};

function OrganizationVerkkolaskuTab({ t }) {
  const { currentOrganization, fetchUser: refreshOrganization } = useUserStore();
  const organizationId = currentOrganization?.organization_id;

  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [status, setStatus] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [amiliDialogOpen, setAmiliDialogOpen] = useState(false);
  const [amiliDeactivateOpen, setAmiliDeactivateOpen] = useState(false);
  const [amiliChecking, setAmiliChecking] = useState(false);
  const [amiliActivationUrl, setAmiliActivationUrl] = useState(null);
  const pollingRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    if (!organizationId) return;

    try {
      setLoading(true);
      const response = await axios.get(
        `/api/organizations/${organizationId}/maventa/status/`
      );
      setStatus(response.data);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch Maventa status:", err);
      setError(t("Failed to load verkkolaskutus status"));
    } finally {
      setLoading(false);
    }
  }, [organizationId, t]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Polling for pending states
  useEffect(() => {
    const isPending =
      status?.maventa_status === STATUS.PENDING_SIGNUP ||
      status?.maventa_status === STATUS.AWAITING_SIGNATURE;

    if (isPending && !pollingRef.current) {
      // Poll every 30 seconds while pending
      pollingRef.current = setInterval(() => {
        fetchStatus();
      }, 30000);
    } else if (!isPending && pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [status?.maventa_status, fetchStatus]);

  // Fetch Amili activation URL when status is pending
  useEffect(() => {
    if (currentOrganization?.amili_status === "pending" && organizationId && !amiliActivationUrl) {
      axios.get(`/api/organizations/${organizationId}/amili/status/`)
        .then((response) => {
          if (response.data.activation_url) {
            setAmiliActivationUrl(response.data.activation_url);
          }
        })
        .catch(() => {});
    }
  }, [currentOrganization?.amili_status, organizationId, amiliActivationUrl]);

  const handleActivationSuccess = async (data) => {
    setSuccessMessage(
      t("Activation started! Check your email for the Visma Sign link.")
    );
    await fetchStatus();
    if (refreshOrganization) {
      await refreshOrganization();
    }
  };

  const handleLinkExistingSuccess = async (data) => {
    setSuccessMessage(
      t("maventa.linkSuccess", "Maventa-tili yhdistetty onnistuneesti! Voit nyt lähettää verkkolaskuja.")
    );
    await fetchStatus();
    if (refreshOrganization) {
      await refreshOrganization();
    }
  };

  const handleResendEmail = async () => {
    setResending(true);
    setError(null);

    try {
      const response = await axios.post(
        `/api/organizations/${organizationId}/maventa/resend-auth/`
      );
      if (response.data.success) {
        setSuccessMessage(t("Authorization email sent! Check your inbox."));
      } else {
        setError(response.data.error || t("Failed to resend email"));
      }
    } catch (err) {
      console.error("Failed to resend auth email:", err);
      setError(
        err.response?.data?.error ||
          t("Failed to resend email. Please try again.")
      );
    } finally {
      setResending(false);
    }
  };

  const handleCheckStatus = async () => {
    setChecking(true);
    setError(null);

    try {
      await fetchStatus();
    } finally {
      setChecking(false);
    }
  };

  const handleRetryActivation = () => {
    setDialogOpen(true);
  };

  const handleAmiliActivationSuccess = async (data) => {
    // Show KYC link immediately from activation response
    if (data?.activation_url) {
      setAmiliActivationUrl(data.activation_url);
    }
    setSuccessMessage(
      t("amili.activationStarted", "Maksuvalvonta-aktivointi aloitettu!")
    );
    if (refreshOrganization) {
      await refreshOrganization();
    }
  };

  const handleAmiliDeactivated = async (data) => {
    const count = data?.open_assignments || 0;
    setSuccessMessage(
      count > 0
        ? t("amili.deactivatedWithOpen", {
            count,
            defaultValue:
              "Maksuvalvonta on lopetettu. {{count}} laskua jää Amilin hoidettavaksi, ja niiden tapahtumat näkyvät edelleen laskuilla.",
          })
        : t("amili.deactivated", "Maksuvalvonta on lopetettu.")
    );
    setAmiliActivationUrl(null);
    if (refreshOrganization) {
      await refreshOrganization();
    }
  };

  const handleCheckAmiliStatus = async () => {
    setAmiliChecking(true);
    setError(null);
    try {
      const response = await axios.get(
        `/api/organizations/${organizationId}/amili/status/`
      );
      if (response.data.activation_url) {
        setAmiliActivationUrl(response.data.activation_url);
      }
      if (response.data.amili_status === "active") {
        setSuccessMessage(t("amili.nowActive", "Maksuvalvonta on nyt aktiivinen!"));
        setAmiliActivationUrl(null);
        if (refreshOrganization) {
          await refreshOrganization();
        }
      } else if (response.data.amili_status === "pending") {
        setSuccessMessage(t("amili.stillPending", "KYC-tunnistautuminen on vielä kesken."));
      }
    } catch (err) {
      setError(err.response?.data?.error || t("amili.statusCheckFailed", "Tilan tarkistus epäonnistui"));
    } finally {
      setAmiliChecking(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const einvoicingAvailable = status?.einvoicing_available ?? true;
  const maventaStatus = status?.maventa_status || STATUS.NOT_ACTIVATED;
  const maventaEnabled = status?.maventa_enabled || false;
  const bankNetworkActive = status?.bank_network_active || false;
  const ovtAddress = status?.ovt_address || "";
  const authEmail = status?.auth_email || "";
  const activationError = status?.activation_error || "";

  // Render status chip based on maventa_status
  const renderStatusChip = () => {
    switch (maventaStatus) {
      case STATUS.ACTIVE:
        return (
          <Chip
            icon={<CheckIcon />}
            label={t("Active")}
            color="success"
            size="small"
          />
        );
      case STATUS.PENDING_SIGNUP:
        return (
          <Chip
            icon={<EmailIcon />}
            label={t("Check your email")}
            color="warning"
            size="small"
          />
        );
      case STATUS.AWAITING_SIGNATURE:
        return (
          <Chip
            icon={<ScheduleIcon />}
            label={t("Awaiting signature")}
            color="warning"
            size="small"
          />
        );
      case STATUS.FAILED:
        return (
          <Chip
            icon={<ErrorIcon />}
            label={t("Failed")}
            color="error"
            size="small"
          />
        );
      default:
        return (
          <Chip
            label={t("Not activated")}
            size="small"
            sx={{ bgcolor: "grey.200" }}
          />
        );
    }
  };

  // Render description based on status
  const renderDescription = () => {
    switch (maventaStatus) {
      case STATUS.ACTIVE:
        return t(
          "Your organization is connected to Maventa e-invoice network"
        );
      case STATUS.PENDING_SIGNUP:
      case STATUS.AWAITING_SIGNATURE:
        return (
          <>
            {t(
              "A signing request has been sent to your email. Please check your inbox and sign with your bank credentials to complete activation."
            )}
            {authEmail && (
              <Typography
                variant="body2"
                sx={{ mt: 1, fontWeight: 500 }}
              >
                {t("Email sent to")}: {authEmail}
              </Typography>
            )}
          </>
        );
      case STATUS.FAILED:
        return activationError || t("Activation failed. Please try again.");
      default:
        return t(
          "Activate verkkolaskutus to send electronic invoices to companies and insurance providers"
        );
    }
  };

  // Render action button based on status
  const renderActionButton = () => {
    const isPending =
      maventaStatus === STATUS.PENDING_SIGNUP ||
      maventaStatus === STATUS.AWAITING_SIGNATURE;

    if (maventaStatus === STATUS.NOT_ACTIVATED) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "flex-start" }}>
          <Button
            variant="contained"
            onClick={() => setDialogOpen(true)}
            disabled={!einvoicingAvailable}
          >
            {t("Activate Verkkolaskutus")}
          </Button>
          <Link
            component="button"
            variant="body2"
            onClick={() => setLinkDialogOpen(true)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
            disabled={!einvoicingAvailable}
          >
            <LinkIcon fontSize="small" />
            {t("maventa.alreadyHaveAccount", "Onko sinulla jo Maventa-tili?")}
          </Link>
        </Box>
      );
    }

    if (isPending) {
      return (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            onClick={handleResendEmail}
            disabled={resending}
            startIcon={
              resending ? <CircularProgress size={16} /> : <EmailIcon />
            }
          >
            {resending ? <span>{t("Sending...")}</span> : <span>{t("Resend Email")}</span>}
          </Button>
          <Button
            variant="text"
            onClick={handleCheckStatus}
            disabled={checking}
            startIcon={
              checking ? <CircularProgress size={16} /> : <RefreshIcon />
            }
          >
            {t("Check Status")}
          </Button>
        </Box>
      );
    }

    if (maventaStatus === STATUS.FAILED) {
      return (
        <Button
          variant="contained"
          onClick={handleRetryActivation}
        >
          {t("Retry Activation")}
        </Button>
      );
    }

    return null;
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, color: "text.secondary" }}
        >
          {t("Verkkolaskutus")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t(
            "Send electronic invoices through the Finnish banking network to companies and insurance providers"
          )}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          onClose={() => setSuccessMessage(null)}
        >
          {successMessage}
        </Alert>
      )}

      {!einvoicingAvailable && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
            {t("einvoicing.testingPhaseTitle", "E-invoicing is in testing phase")}
          </Typography>
          <Typography variant="body2">
            {t("einvoicing.testingPhaseDescription", "E-invoicing (verkkolaskutus) is currently being tested and is not yet available for production use. We will notify you when the service is ready.")}
          </Typography>
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Maventa Activation Card */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: "100%" }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor:
                      maventaStatus === STATUS.ACTIVE
                        ? "success.main"
                        : maventaStatus === STATUS.FAILED
                        ? "error.main"
                        : maventaStatus === STATUS.PENDING_SIGNUP ||
                          maventaStatus === STATUS.AWAITING_SIGNATURE
                        ? "warning.main"
                        : "grey.400",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                  }}
                >
                  <ReceiptIcon />
                </Box>
                {renderStatusChip()}
              </Box>

              <Typography variant="h6" gutterBottom>
                {t("E-Invoice Service")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {renderDescription()}
              </Typography>

              {maventaStatus === STATUS.ACTIVE && ovtAddress && (
                <Box
                  sx={{
                    bgcolor: "grey.100",
                    p: 2,
                    borderRadius: 1,
                    mb: 2,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {t("Your OVT Address")}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ fontFamily: "monospace", fontWeight: 600 }}
                  >
                    {ovtAddress}
                  </Typography>
                </Box>
              )}

              {renderActionButton()}
            </CardContent>
          </Card>
        </Grid>

        {/* Bank Network Card */}
        <Grid item xs={12} md={6}>
          <Card
            variant="outlined"
            sx={{
              height: "100%",
              opacity: maventaEnabled ? 1 : 0.6,
            }}
          >
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: bankNetworkActive
                      ? "success.main"
                      : maventaEnabled
                      ? "warning.main"
                      : "grey.400",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                  }}
                >
                  <BankIcon />
                </Box>
                {bankNetworkActive ? (
                  <Chip
                    icon={<CheckIcon />}
                    label={t("Active")}
                    color="success"
                    size="small"
                  />
                ) : maventaEnabled ? (
                  <Chip
                    icon={<ScheduleIcon />}
                    label={t("Pending")}
                    color="warning"
                    size="small"
                  />
                ) : (
                  <Chip
                    label={t("Requires activation")}
                    size="small"
                    sx={{ bgcolor: "grey.200" }}
                  />
                )}
              </Box>

              <Typography variant="h6" gutterBottom>
                {t("Bank Network")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {bankNetworkActive
                  ? t(
                      "You can send invoices through the Finnish bank network"
                    )
                  : maventaEnabled
                  ? t(
                      "Bank network activation is in progress. This typically takes 1-3 business days after signing."
                    )
                  : t(
                      "Bank network will be activated after completing the Visma Sign verification"
                    )}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Information Section */}
      <Box sx={{ mt: 4 }}>
        <Divider sx={{ mb: 3 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
          {t("How it works")}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  bgcolor: "primary.main",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                1
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {t("Create invoice")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("Generate invoice from completed maintenance")}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  bgcolor: "primary.main",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                2
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {t("Choose recipient")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("Look up company e-invoice address by Y-tunnus")}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  bgcolor: "primary.main",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                3
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {t("Send electronically")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("Invoice delivered securely through bank network")}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Pricing Note */}
      {maventaEnabled && (
        <Alert severity="info" sx={{ mt: 3 }}>
          {t(
            "E-invoices are billed per invoice sent. Check your subscription plan for pricing details."
          )}
        </Alert>
      )}

      {/* Maksuvalvonta Section - always visible */}
      <Box sx={{ mt: 4 }}>
        <Divider sx={{ mb: 3 }} />
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, color: "text.secondary" }}
          >
            {t("amili.sectionTitle", "Maksuvalvonta")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t(
              "amili.sectionDescription",
              "Amili Kassavirta -palvelu hoitaa laskujesi maksuvalvonnan, maksumuistutukset ja perinnän automaattisesti."
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t(
              "amili.sectionCostNote",
              "Kun maksuvalvonta on käytössä, kaikki laskut lähetetään Maventan kautta (0,50 €/lasku)."
            )}
          </Typography>
          <Link
            href="https://maventa.fi/muistutus-ja-perinta/amili"
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              mt: 0.5,
            }}
          >
            {t("amili.learnMore", "Lue lisää Visma Amilista")}
            <OpenInNewIcon fontSize="inherit" />
          </Link>
        </Box>

        <Card variant="outlined">
          <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor:
                      currentOrganization?.amili_status === "active"
                        ? "success.main"
                        : currentOrganization?.amili_status === "pending"
                        ? "warning.main"
                        : currentOrganization?.amili_status === "failed"
                        ? "error.main"
                        : "grey.400",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                  }}
                >
                  <BankIcon />
                </Box>
                {currentOrganization?.amili_status === "active" ? (
                  <Chip
                    icon={<CheckIcon />}
                    label={t("Active")}
                    color="success"
                    size="small"
                  />
                ) : currentOrganization?.amili_status === "pending" ? (
                  <Chip
                    icon={<ScheduleIcon />}
                    label={t("amili.pendingKyc", "KYC-tunnistautuminen")}
                    color="warning"
                    size="small"
                  />
                ) : currentOrganization?.amili_status === "failed" ? (
                  <Chip
                    icon={<ErrorIcon />}
                    label={t("Failed")}
                    color="error"
                    size="small"
                  />
                ) : (
                  <Chip
                    label={t("Not activated")}
                    size="small"
                    sx={{ bgcolor: "grey.200" }}
                  />
                )}
              </Box>

              <Typography variant="h6" gutterBottom>
                {t("amili.cardTitle", "Maksuvalvonta")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {currentOrganization?.amili_status === "active"
                  ? t(
                      "amili.activeDescription",
                      "Maksuvalvonta on käytössä. Kaikki Maventan kautta lähetetyt laskut ovat automaattisesti maksuvalvonnassa."
                    )
                  : currentOrganization?.amili_status === "pending"
                  ? t(
                      "amili.pendingDescription",
                      "Aktivointi on käynnissä. Suorita KYC-tunnistautuminen alla olevan linkin kautta."
                    )
                  : currentOrganization?.amili_status === "failed"
                  ? currentOrganization?.amili_activation_error ||
                    t("amili.failedDescription", "Aktivointi epäonnistui. Yritä uudelleen.")
                  : t(
                      "amili.notActivatedDescription",
                      "Aktivoi maksuvalvonta saadaksesi automaattisen maksuvalvonnan, maksumuistutukset ja perinnän laskuillesi."
                    )}
              </Typography>

              {currentOrganization?.amili_status === "not_activated" && maventaStatus === STATUS.ACTIVE && (
                <Button
                  variant="contained"
                  onClick={() => setAmiliDialogOpen(true)}
                >
                  {t("amili.activate", "Aktivoi")}
                </Button>
              )}

              {currentOrganization?.amili_status === "active" && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "flex-start" }}>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => setAmiliDeactivateOpen(true)}
                    data-testid="amili-deactivate-button"
                  >
                    {t("amili.deactivate", "Lopeta maksuvalvonta")}
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    {t(
                      "amili.deactivateHint",
                      "Jo maksuvalvonnassa olevat laskut jäävät Amilille. Näet seuraukset ennen vahvistamista."
                    )}
                  </Typography>
                </Box>
              )}

              {currentOrganization?.amili_status === "not_activated" && maventaStatus !== STATUS.ACTIVE && (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                  {t("amili.requiresMaventa", "Aktivoi ensin verkkolaskutus käyttääksesi maksuvalvontaa.")}
                </Typography>
              )}

              {currentOrganization?.amili_status === "pending" && (
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  {amiliActivationUrl && (
                    <Button
                      variant="contained"
                      href={amiliActivationUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t("amili.completeKyc", "Suorita tunnistautuminen")}
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    onClick={handleCheckAmiliStatus}
                    disabled={amiliChecking}
                    startIcon={
                      amiliChecking ? (
                        <CircularProgress size={16} />
                      ) : (
                        <RefreshIcon />
                      )
                    }
                  >
                    {t("Check Status")}
                  </Button>
                </Box>
              )}

              {currentOrganization?.amili_status === "pending" && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {t("amili.pendingNote", "Jos olet jo suorittanut tunnistautumisen, tila päivittyy automaattisesti 1–2 arkipäivän kuluessa.")}
                </Alert>
              )}

              {currentOrganization?.amili_status === "failed" && (
                <Button
                  variant="contained"
                  onClick={() => setAmiliDialogOpen(true)}
                >
                  {t("Retry Activation")}
                </Button>
              )}
            </CardContent>
          </Card>
        </Box>

      {/* Activation Dialog */}
      <MaventaActivationDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSuccess={handleActivationSuccess}
      />

      {/* Link Existing Account Dialog */}
      <MaventaLinkExistingDialog
        open={linkDialogOpen}
        onClose={() => setLinkDialogOpen(false)}
        onSuccess={handleLinkExistingSuccess}
      />

      {/* Amili Activation Dialog */}
      <AmiliActivationDialog
        open={amiliDialogOpen}
        onClose={() => setAmiliDialogOpen(false)}
        onSuccess={handleAmiliActivationSuccess}
      />

      {/* Amili Deactivation Dialog */}
      <AmiliDeactivationDialog
        open={amiliDeactivateOpen}
        onClose={() => setAmiliDeactivateOpen(false)}
        onSuccess={handleAmiliDeactivated}
      />
    </Box>
  );
}

export default withTranslation()(OrganizationVerkkolaskuTab);
